import { useCallback, useEffect, useRef, useState } from 'react';

export const SAVING = 'saving';
export const SAVED = 'saved';
export const SAVE_FAILED = 'error';

// How long a control keeps its "Saved" tick before going quiet again.
export const SAVED_TTL_MS = 2500;

/**
 * Drives a settings screen that writes on every change instead of behind a Save
 * button.
 *
 * Two things make this more than a call to `save()` in an onChange handler:
 *
 * 1. The endpoint replaces the WHOLE settings document, so two writes must never
 *    be in flight together — the slower one lands last and silently undoes the
 *    faster one's field. Writes are therefore serialized, and changes made while
 *    one is running are MERGED into a single trailing write rather than queued
 *    up behind it. Flipping five switches quickly is one extra request, not five.
 *
 * 2. Without a Save button, a control still showing the new value after a failed
 *    write is a lie. `onFailure` is where the caller puts the screen back to what
 *    the server actually holds.
 *
 * `save(patch)` must resolve truthy on success. `patch` is the merged set of
 * fields changed since the last write started, so the caller builds its payload
 * from current state plus the patch — React state has not re-rendered yet when a
 * change handler queues its save.
 */
function useSettingsAutosave({ save, onFailure, onIdle }) {
  const [fieldStatus, setFieldStatus] = useState({});
  const [pageStatus, setPageStatus] = useState(null);

  // The callbacks close over component state, so they are re-created every
  // render. Held in refs and read at call time, the queue always writes the
  // latest values instead of whatever existed when a change was queued.
  const saveRef = useRef(save);
  const failureRef = useRef(onFailure);
  const idleRef = useRef(onIdle);
  useEffect(() => {
    saveRef.current = save;
    failureRef.current = onFailure;
    idleRef.current = onIdle;
  });

  const inFlight = useRef(false);
  const pending = useRef(null);
  const timers = useRef({});
  const alive = useRef(true);

  useEffect(
    () => () => {
      alive.current = false;
      Object.values(timers.current).forEach(clearTimeout);
    },
    []
  );

  const markFields = useCallback((keys, status) => {
    if (!keys?.length) return;
    setFieldStatus((prev) => {
      const next = { ...prev };
      keys.forEach((key) => {
        next[key] = status;
      });
      return next;
    });
  }, []);

  // A "Saved" tick is transient; an error stays until the next write clears it.
  const scheduleClear = useCallback((keys) => {
    keys.forEach((key) => {
      clearTimeout(timers.current[key]);
      timers.current[key] = setTimeout(() => {
        delete timers.current[key];
        if (!alive.current) return;
        setFieldStatus((prev) => {
          if (prev[key] !== SAVED) return prev;
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }, SAVED_TTL_MS);
    });
  }, []);

  const flush = useCallback(async () => {
    if (inFlight.current || !pending.current) return;
    const { patch, keys } = pending.current;
    pending.current = null;
    inFlight.current = true;

    markFields(keys, SAVING);
    setPageStatus(SAVING);

    let ok = false;
    try {
      ok = !!(await saveRef.current(patch));
    } catch {
      ok = false;
    }

    if (alive.current) {
      markFields(keys, ok ? SAVED : SAVE_FAILED);
      setPageStatus(ok ? SAVED : SAVE_FAILED);
      if (ok) scheduleClear(keys);
    }

    // The queue stays CLOSED across the failure handler. That handler puts the
    // screen back to what the server holds, and the next payload is built from
    // that screen — so releasing here would let a change made during the revert
    // start a write carrying the value the server has just rejected, and report
    // it saved. Screen and server then disagree with nothing on either saying so.
    if (!ok) await failureRef.current?.(patch, keys);

    inFlight.current = false;

    if (pending.current) {
      flush();
    } else {
      // Not held across this one. It re-reads the profile into redux, which is
      // not where a payload comes from, and holding the queue for a GET would
      // stall the next switch the user flips behind it.
      await idleRef.current?.(ok);
    }
  }, [markFields, scheduleClear]);

  /**
   * Record a change and write it. `patch` is merged into anything already
   * waiting; `keys` are the controls that should show the outcome.
   */
  const queueSave = useCallback(
    (patch, keys = []) => {
      const waiting = pending.current;
      pending.current = {
        patch: { ...waiting?.patch, ...patch },
        keys: Array.from(new Set([...(waiting?.keys || []), ...keys]))
      };
      flush();
    },
    [flush]
  );

  return { queueSave, fieldStatus, pageStatus };
}

export default useSettingsAutosave;
